<template>
  <Modal :show="show" max-width="4xl" @close="$emit('close')">
    <div class="bg-white dark:bg-gray-900">

      <!-- Modal header -->
      <div class="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Business Banner</h2>
        <div class="flex gap-3 items-center">
          <button
            @click="downloadImage"
            :disabled="!!exporting || !qrDataUrl"
            class="inline-flex gap-2 items-center px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg transition-colors hover:bg-emerald-700 disabled:opacity-50">
            <ArrowDownTrayIcon class="w-4 h-4" />
            {{ exporting === 'image' ? 'Exporting…' : 'Download PNG' }}
          </button>
          <button
            @click="downloadPDF"
            :disabled="!!exporting || !qrDataUrl"
            class="inline-flex gap-2 items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg transition-colors hover:bg-indigo-700 disabled:opacity-50">
            <DocumentArrowDownIcon class="w-4 h-4" />
            {{ exporting === 'pdf' ? 'Exporting…' : 'Download PDF' }}
          </button>
          <button @click="$emit('close')" class="p-1 text-gray-400 rounded-full hover:text-gray-600 dark:hover:text-gray-300">
            <XMarkIcon class="w-5 h-5" />
          </button>
        </div>
      </div>

      <!-- Scroll area -->
      <div class="flex flex-col items-center p-6 overflow-y-auto max-h-[84vh]">
        <p class="mb-5 text-xs text-center text-gray-400 dark:text-gray-500">
          Preview — exported at 2× resolution
        </p>

        <!-- Spinner while QR generates -->
        <div v-if="!qrDataUrl" class="flex justify-center items-center py-20">
          <div class="w-10 h-10 rounded-full border-2 border-gray-200 animate-spin border-t-emerald-500"></div>
        </div>

        <!-- ================================================================
             BANNER CANVAS
             All color classes use hex arbitrary values (bg-[#…] / text-[#…])
             so the emitted CSS contains plain hex — not oklch() — which
             html2canvas can parse. Never use named palette utilities here.
             ================================================================ -->
        <div
          v-show="qrDataUrl"
          ref="bannerRef"
          class="w-[480px] border border-[#d1d5db] font-sans"
          style="background-color:#ffffff;color:#111827;">

          <!-- Top accent stripe -->
          <div class="h-2 bg-[linear-gradient(90deg,#059669,#10B981,#34D399,#10B981,#059669)]"></div>

          <!-- ── Brand header ── -->
          <div class="flex items-center justify-between px-8 py-[22px] bg-[#f0fdf4] border-b border-[#bbf7d0]">
            <div class="flex items-center gap-[10px]">
              <div class="flex items-center justify-center w-10 h-10 bg-[#dcfce7] border border-[#6ee7b7] rounded-[10px]">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#059669" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M2 17L12 22L22 17" stroke="#059669" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M2 12L12 17L22 12" stroke="#059669" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <div>
                <div class="text-xl font-black leading-none tracking-tight text-[#059669]">Boxbuddy</div>
                <div class="mt-0.5 text-[9px] uppercase tracking-[2px] text-[#6b7280]">Food &amp; Delivery</div>
              </div>
            </div>
          </div>

          <!-- ── Business identity ── -->
          <div class="px-8 pt-7 pb-5 bg-[#ffffff] text-center border-b border-[#f3f4f6]">

            <!-- Logo / initials -->
            <div class="mb-3 text-[30px] font-black leading-[1.15] tracking-tight text-[#111827]">
              {{ business?.name }}
            </div>

            <!-- Eyebrow -->
            <div class="mb-2 text-[10px] font-bold uppercase tracking-[3px] text-[#10b981]">
              Now Available on Boxbuddy
            </div>


            <!-- Description -->
            <div v-if="business?.description" class="mb-[18px] text-[12.5px] leading-[1.55] text-[#6b7280]">
              {{ truncate(business.description, 120) }}
            </div>
          </div>


          <!-- ── QR centrepiece ── -->
          <div class="flex flex-col items-center px-8 pt-5 pb-6 bg-[#ffffff]">
            <!-- Outer coloured ring -->
            <div class="p-1.5 bg-[#ecfdf5] border-[3px] border-[#10b981] rounded-[20px] shadow-[0_8px_28px_rgba(16,185,129,0.18)]">
              <!-- Inner white mat -->
              <div class="p-3 bg-[#ffffff] rounded-[14px] border border-[#d1fae5]">
                <img v-if="qrDataUrl" :src="qrDataUrl" alt="QR Code" class="block w-[220px] h-[220px]" />
              </div>
            </div>
            <!-- Caption -->
            <div class="mt-4 text-center">
              <div class="text-[13px] font-bold text-[#059669]">Point your camera here to order</div>
              <div class="mt-0.5 text-[11px] text-[#9ca3af]">Works with any smartphone camera app</div>
            </div>
          </div>
          <!-- Bottom accent stripe -->
          <div class="h-2 bg-[linear-gradient(90deg,#059669,#10B981,#34D399,#10B981,#059669)]"></div>
        </div>
        <!-- end banner canvas -->
      </div>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { ArrowDownTrayIcon, DocumentArrowDownIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import Modal from '@/components/general/Modal.vue'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import QRCodeLib from 'qrcode'

const props = defineProps({
  show: { type: Boolean, default: false },
  business: { type: Object, default: null },
})
defineEmits(['close'])

const bannerRef = ref(null)
const exporting = ref<string | null>(null)
const qrDataUrl = ref('')

const orderUrl = computed(() => {
  if (!props.business?.uuid) return window.location.origin
  return `${window.location.origin}/businesses/${props.business.uuid}`
})


function truncate(str: string, max: number) {
  if (!str || str.length <= max) return str
  return str.slice(0, max).trimEnd() + '…'
}

async function generateQR() {
  if (!props.business?.uuid) return
  qrDataUrl.value = ''
  qrDataUrl.value = await QRCodeLib.toDataURL(orderUrl.value, {
    width: 440,
    margin: 2,
    color: { dark: '#111827', light: '#ffffff' },
    errorCorrectionLevel: 'H',
  })
}

watch(
  () => [props.show, props.business?.uuid],
  ([isOpen]) => { if (isOpen) generateQR() },
  { immediate: true },
)

async function renderCanvas() {
  await new Promise((r) => setTimeout(r, 80))
  return html2canvas(bannerRef.value as HTMLElement, {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#ffffff',
    logging: false,
    imageTimeout: 6000,
    onclone: (_doc: Document, el: HTMLElement) => {
      el.querySelectorAll<HTMLElement>('*').forEach((child) => {
        const cs = window.getComputedStyle(child)
        if (cs.overflow === 'hidden' && cs.borderRadius && cs.borderRadius !== '0px') {
          child.style.overflow = 'visible'
        }
      })
    },
  })
}

async function downloadImage() {
  if (exporting.value || !qrDataUrl.value) return
  exporting.value = 'image'
  try {
    const canvas = await renderCanvas()
    const link = document.createElement('a')
    link.download = `${props.business?.name ?? 'business'}-banner.png`
    link.href = canvas.toDataURL('image/png')
    link.click()
  } finally {
    exporting.value = null
  }
}

async function downloadPDF() {
  if (exporting.value || !qrDataUrl.value) return
  exporting.value = 'pdf'
  try {
    const canvas = await renderCanvas()
    const imgData = canvas.toDataURL('image/png')
    const pxW = canvas.width
    const pxH = canvas.height
    const mmW = 148
    const mmH = Math.round((pxH / pxW) * mmW)
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [mmW, mmH] })
    pdf.addImage(imgData, 'PNG', 0, 0, mmW, mmH)
    pdf.save(`${props.business?.name ?? 'business'}-banner.pdf`)
  } finally {
    exporting.value = null
  }
}
</script>
